import { Controller } from "@hotwired/stimulus"
import ProgressBar from "../helpers/progress_bar"

const JQUERY_NAMESPACE = ".thankYous"

// Paired with the shared `datatable` controller on the same wrapping element: that one owns the
// DataTables lifecycle, this one owns the date-range picker and the two select2 filters.
//
// Both filters really do narrow the table: Gift.datatable_relation reads
// params[:filter][:sender]/[:responded] and params[:range]. The range matches gift
// created_at/scheduled_at/sent_at, NOT thanked_at, despite this being the thank-you-notes report.
export default class extends Controller {
  static get targets() {
    return ["table", "dateRangeInput", "senderFilter", "respondedFilter"];
  }

  static get values() {
    return { begin: String, end: String };
  }

  static get classes() {
    return ["open"];
  }

  connect() {
    $(this.dateRangeInputTarget).daterangepicker({
      buttonClasses: "btn btn-main btn-md",
      cancelButtonClasses: "btn-ghost",
      opens: "left",
      alwaysShowCalendars: true,
      showCustomRangeLabel: false,
      ranges: {
        "Today": [moment(), moment()],
        "Yesterday": [moment().subtract(1, "days"), moment().subtract(1, "days")],
        "Last 7 Days": [moment().subtract(6, "days"), moment()],
        "Last 30 Days": [moment().subtract(29, "days"), moment()],
        "This Month": [moment().startOf("month"), moment().endOf("month")],
        "Last Month": [moment().subtract(1, "month").startOf("month"), moment().subtract(1, "month").endOf("month")],
        "All Time": [moment(this.beginValue, "MM-DD-YYYY"), moment(this.endValue, "MM-DD-YYYY")],
      },
    }, (start, end) => this.applyRange(start, end));

    // show.daterangepicker/hide.daterangepicker are announced by the plugin through jQuery's
    // trigger, not a real DOM event, so the binding itself has to stay on jQuery -- but the class
    // toggle it runs is plain DOM work on our own static markup.
    $(this.dateRangeInputTarget).
      on(`show.daterangepicker${JQUERY_NAMESPACE}`, () => this.element.querySelector(".date-range-picker").classList.add(this.openClass)).
      on(`hide.daterangepicker${JQUERY_NAMESPACE}`, () => this.element.querySelector(".date-range-picker").classList.remove(this.openClass));

    $(this.senderFilterTarget).select2({ placeholder: "Sender", width: "100%" });
    $(this.respondedFilterTarget).select2({
      placeholder: "Response",
      width: "100%",
      minimumResultsForSearch: Infinity,
      allowClear: true,
    });

    // select2 announces a pick with jQuery's `.trigger("change")`, not a native dispatch, so this
    // has to stay on jQuery's event bus rather than becoming a data-action.
    $(this.element).on(`change${JQUERY_NAMESPACE}`, ".datatable-filter", () => this.reload());
  }

  disconnect() {
    const picker = $(this.dateRangeInputTarget).data("daterangepicker");
    if (picker) { picker.remove(); }
    $(this.dateRangeInputTarget).off(JQUERY_NAMESPACE);

    $(this.element).off(JQUERY_NAMESPACE);

    if ($(this.senderFilterTarget).data("select2")) {
      $(this.senderFilterTarget).select2("destroy");
    }
    if ($(this.respondedFilterTarget).data("select2")) {
      $(this.respondedFilterTarget).select2("destroy");
    }
  }

  applyRange(start, end) {
    const newRange = `${start.format("MMDDYYYY")}-${end.format("MMDDYYYY")}`;
    this.reload({ range: newRange });
  }

  reload(extraParams = {}) {
    ProgressBar.start();

    const $filterSelects = $(this.element).find(".datatable-moveable-filter, .datatable-filter");
    if ($filterSelects.length > 0) {
      const filter = {};
      $filterSelects.each((_, el) => {
        const $select = $(el);
        const key = $select.attr("name").replace(/(^.*\[|\].*$)/g, "");
        filter[key] = ($select.select2("data") || []).map((option) => option.id);
      });
      extraParams["filter"] = filter;
    }

    $(this.element).find(".datatable-moveable-text-filter, .datatable-text-filter").each((_, el) => {
      const $text = $(el);
      extraParams["filter"][$text.attr("name").replace(/(^.*\[|\].*$)/g, "")] = $text.val();
    });

    const params = Object.assign(this.currentParams(), extraParams);

    const replacement = `${this.tableTarget.getAttribute("data-source").split("?")[0]}?${$.param(params)}`;
    $(this.tableTarget).attr("data-source", replacement);
    $(this.tableTarget).DataTable().ajax.url(replacement).load(() => ProgressBar.stop());
  }

  // `data-source` on the table target is the app-wide datatable convention (decisions.md D8), not
  // a Values API candidate -- 15+ other datatable-driving controllers and several specs read/assert
  // this exact attribute name directly.
  currentParams() {
    return this.urlParamsAsObject(this.tableTarget.getAttribute("data-source"));
  }

  // Converts a bracket-notation query string (filter[sender][]=3) into the nested object
  // ({ filter: { sender: ["3"] } }) that `$.param()` re-serializes back into the same form. A
  // URLSearchParams rewrite would lose the nesting.
  urlParamsAsObject(dataSource) {
    const query = dataSource.substring(dataSource.indexOf("?") + 1);
    const regex = /([^&=]+)=?([^&]*)/g;
    const decode = (str) => decodeURIComponent(str.replace(/\+/g, " "));

    const params = {};
    let match = regex.exec(query);
    while (match) {
      const k = decode(match[1]);
      const v = decode(match[2]);

      if (k.substring(k.length - 2) === "[]") {
        const key = k.substring(0, k.length - 2);
        (params[key] || (params[key] = [])).push(v);
      } else {
        params[k] = v;
      }

      match = regex.exec(query);
    }

    const assignPath = (obj, keyPath, value) => {
      const lastKeyIndex = keyPath.length - 1;
      let target = obj;
      for (let i = 0; i < lastKeyIndex; i++) {
        const key = keyPath[i];
        if (!(key in target)) { target[key] = {}; }
        target = target[key];
      }
      target[keyPath[lastKeyIndex]] = value;
    };

    Object.keys(params).forEach((prop) => {
      const structure = prop.split("[");
      if (structure.length > 1) {
        const levels = structure.map((part) => part.replace(/[?[\]\\ ]/g, ""));
        assignPath(params, levels, params[prop]);
        delete params[prop];
      }
    });

    return params;
  }
}
